import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-services/commerce-services/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "export-api"
    }}>{`Export API`}</h1>
    <p>{`The Adobe Commerce Reporting Export API allows you to access some of the raw data behind your Adobe Commerce Reporting dashboard.`}</p>
    <p>{`Before using the export API, make sure you `}<a parentName="p" {...{
        "href": "../reporting/index.md#authentication"
      }}>{`authenticate`}</a>{` your connection.`}</p>
    <h2 {...{
      "id": "raw-data-exports"
    }}>{`Raw data exports`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`You need a `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-business-intelligence/mbi/tutorials/export-raw-data"
      }}>{`raw data export`}</a>{` before calling the following methods.`}</p>
    <h3 {...{
      "id": "list-all-available-raw-exports"
    }}>{`List all available raw exports`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /export`}</inlineCode>{` call returns a list of raw exports available.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /export`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/export
`}</code></pre>
    <h3 {...{
      "id": "get-a-specific-export"
    }}>{`Get a specific export`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /export/:id`}</inlineCode>{`call returns a zip-compressed CSV file containing the raw data of a completed export.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /export/:id`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/export/51
`}</code></pre>
    <h3 {...{
      "id": "get-an-export-description"
    }}>{`Get an export description`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /export/:id/info`}</inlineCode>{` call returns a description of the export with the specified ID.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /export/:id/info`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/export/51/info
`}</code></pre>
    <h3 {...{
      "id": "copy-an-export"
    }}>{`Copy an export`}</h3>
    <p>{`Creates a new export with the exact same parameters as the specified export. If you specify the `}<inlineCode parentName="p">{`name`}</inlineCode>{` parameter, the new export will be assigned its value.
​`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The API no longer supports modifying the start and end time parameters.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /export/:id/copy/name=<ExportName>`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -d "name=New Copied Export" -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/export/51/copy
`}</code></pre>
    <h2 {...{
      "id": "data-tables"
    }}>{`Data tables`}</h2>
    <h3 {...{
      "id": "get-a-list-of-tables"
    }}>{`Get a list of tables`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /client/:clientId/table`}</inlineCode>{` call returns a list of tables in the data warehouse of the client corresponding to `}<em parentName="p">{`clientId`}</em>{`.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /client/:clientId/table`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/client/12/table
`}</code></pre>
    <h3 {...{
      "id": "get-a-list-of-table-columns"
    }}>{`Get a list of table columns`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /client/:clientId/table/:tableId`}</inlineCode>{` call returns a list of table columns in the table corresponding to `}<em parentName="p">{`tableIid`}</em>{`.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /client/:clientId/table/:tableId`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/client/12/table/3
`}</code></pre>
    <h3 {...{
      "id": "create-a-raw-data-export-of-a-table"
    }}>{`Create a raw data export of a table`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POST /client/:clientId/table/:tableId/export`}</inlineCode>{` call creates a new raw data export of the entire contents of the table corresponding to `}<em parentName="p">{`tableId`}</em>{`.
​`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Exports are capped at 10 million rows. Do not try this on very large tables.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /client/:clientId/table/:tableId/export`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -d "" -H "X-RJM-API-Key: <your_key>" https://api.rjmetrics.com/0.1/client/12/table/3/export
`}</code></pre>
    <h2 {...{
      "id": "figures"
    }}>{`Figures`}</h2>
    <h3 {...{
      "id": "get-a-list-of-figures"
    }}>{`Get a list of figures`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /figure`}</inlineCode>{` call returns a list of figures that are available for export.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /figure`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/figure
`}</code></pre>
    <h3 {...{
      "id": "export-figure-data"
    }}>{`Export figure data`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POST /figure/:id/export/format=<FormatType>`}</inlineCode>{` call outputs the data used to create the figure with the specified ID. Figure IDs can be found in the "Export Figure" dialog in the Adobe Commerce Reporting dashboard interface.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "295px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "100.33898305084745%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/services/static/db88036e5e7017b28f435ef2409ac912/66888/figure-id.webp 295w"],
            "sizes": "(max-width: 295px) 100vw, 295px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/services/static/db88036e5e7017b28f435ef2409ac912/60cc9/figure-id.png 295w"],
            "sizes": "(max-width: 295px) 100vw, 295px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/services/static/db88036e5e7017b28f435ef2409ac912/60cc9/figure-id.png",
            "alt": "Export figure dialog",
            "title": "Export figure dialog",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`If a figure was created using the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-business-intelligence/mbi/build/reports/ess-rpt-build-visual"
      }}>{`Visual Report Builder`}</a>{`, you can specify an output format of either `}<inlineCode parentName="p">{`.csv`}</inlineCode>{` or `}<inlineCode parentName="p">{`.json`}</inlineCode>{`. If a figure was created using the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-business-intelligence/mbi/analyze/sql/sql-rpt-bldr"
      }}>{`SQL Report Builder`}</a>{`, you can specify an output format of `}<inlineCode parentName="p">{`.csv`}</inlineCode>{` only.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /figure/:id/export/format=<FormatType>`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -d "format=csv&includeColumnHeaders=1" -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/figure/360531/export
`}</code></pre>
    <h3 {...{
      "id": "get-a-figure-description"
    }}>{`Get a figure description`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POST /figure/:id/info`}</inlineCode>{` call returns a description of the specified figure. Figure IDs can be found in the "Export Figure" dialog in the Adobe Commerce Reporting dashboard interface.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /figure/:id/info`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/figure/360531/info
`}</code></pre>
    <h2 {...{
      "id": "charts"
    }}>{`Charts`}</h2>
    <p>{`Export Specific Reports (created by cohort report builder)`}</p>
    <h3 {...{
      "id": "get-a-list-of-charts"
    }}>{`Get a list of charts`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`GET /chart`}</inlineCode>{` call returns a list of charts that are available for export.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`GET /chart`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/chart
`}</code></pre>
    <h3 {...{
      "id": "export-chart-data"
    }}>{`Export chart data`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POST /chart/:id/export/format=<FormatType>\\]`}</inlineCode>{` call outputs the data used to create the specified chart. Chart IDs can be found in the "Export Chart" dialog in the Adobe Commerce Reporting dashboard interface.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "299px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "99.33110367892976%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/services/static/c1a2740902d2deb55948c8ad2a11a6f7/120d1/chart-id.webp 299w"],
            "sizes": "(max-width: 299px) 100vw, 299px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/services/static/c1a2740902d2deb55948c8ad2a11a6f7/1413b/chart-id.png 299w"],
            "sizes": "(max-width: 299px) 100vw, 299px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/services/static/c1a2740902d2deb55948c8ad2a11a6f7/1413b/chart-id.png",
            "alt": "Export chart dialog",
            "title": "Export chart dialog",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`The user can specify an output format of either `}<inlineCode parentName="p">{`.csv`}</inlineCode>{` or `}<inlineCode parentName="p">{`.json`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /chart/:id/export/format=<FormatType>\\]`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -d "format=csv&includeColumnHeaders=1" -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/chart/2038112/export
`}</code></pre>
    <h3 {...{
      "id": "get-a-chart-description"
    }}>{`Get a chart description`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POST /chart/:id/info`}</inlineCode>{` call returns a description of the specified chart or a 404 if the chart does not exist. Chart IDs can be found in the "Export Chart" dialog in the Adobe Commerce Reporting dashboard interface.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong>{`
​
`}<inlineCode parentName="p">{`POST /chart/:id/info`}</inlineCode>{`
​
`}<strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -H "X-RJM-API-Key: your\\_key" https://api.rjmetrics.com/0.1/chart/2038112/info
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      